import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useUserProfileContext } from '../../contexts/UserProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import { compressImageIfNeeded } from '../../utils/imageCompressor';
import ApiErrorModal from '../../components/ApiErrorModal';
import ConfirmModal from '../../components/ConfirmModal';
import { CheckIcon, PhoneIcon } from '../../components/Icons';
import CountryCodeSelect from '../../components/CountryCodeSelect';
import CountrySelect from '../../components/CountrySelect';
import EmailManager from '../../components/EmailManager';
import PhoneManager from '../../components/PhoneManager';
import AddressManager from '../../components/AddressManager';
import BankAccountsManager from '../../components/BankAccountsManager';
import PasswordManager from '../../components/PasswordManager';
import { validateAndFormatPhone } from '../../utils/phoneValidation';
import { startRegistration } from '@simplewebauthn/browser';
import { detectUserLocationData } from '../../utils/geo';

// ProfileManager.jsx
// Production-grade profile administration dashboard component.
// Manages identity fields, multi-row contact/address/bank data with validation and console logging.

// ID generator utility (outside component to avoid render-time calls)
const genId = () => `id_${Math.random().toString(36).slice(2, 9)}`;

// Helper to parse dial code and national number from phone strings
const parsePhoneComponents = (phoneStr, dialCode = null, natNum = null) => {
  if (dialCode && natNum !== null && natNum !== undefined) {
    return { countryCode: dialCode, number: natNum };
  }
  const raw = (phoneStr || '').trim();
  if (!raw) return { countryCode: '+1', number: '' };
  const knownPrefixes = [
    '+971', '+880', '+977', '+974', '+966', '+965', '+968', '+973',
    '+358', '+353', '+351', '+420', '+380', '+852', '+886',
    '+44', '+61', '+91', '+94', '+49', '+33', '+81', '+86',
    '+55', '+52', '+65', '+27', '+20', '+39', '+34', '+31',
    '+41', '+46', '+47', '+45', '+64', '+82', '+60', '+62',
    '+63', '+66', '+84', '+92', '+90', '+54', '+56', '+57',
    '+51', '+58', '+48', '+43', '+32', '+30', '+36', '+40',
    '+7', '+1'
  ];
  for (const p of knownPrefixes) {
    if (raw.startsWith(p)) {
      return { countryCode: p, number: raw.slice(p.length) };
    }
  }
  return { countryCode: '+1', number: raw.replace(/^\+/, '') };
};

// Module-level designations cache and ongoing promise to prevent duplicate API requests
let cachedDesignations = null;
let designationsPromise = null;

const fetchDesignationsOnce = async () => {
  if (cachedDesignations && cachedDesignations.length > 0) return cachedDesignations;
  if (!designationsPromise) {
    designationsPromise = api.get('/auth/designations')
      .then((res) => {
        cachedDesignations = res.data?.data || [];
        return cachedDesignations;
      })
      .catch((err) => {
        console.warn('Failed to load designations:', err?.message || err);
        throw err;
      })
      .finally(() => {
        designationsPromise = null;
      });
  }
  return designationsPromise;
};

// Module-level personal identity cache and ongoing promise to prevent duplicate API requests
let currentCacheUserKey = null;
let cachedIdentity = null;
let identityPromise = null;
let cachedEmails = null;
let cachedPhones = null;
let cachedAddresses = null;
let cachedBankAccounts = null;
let cachedBankAccountsPagination = null;
let emailsPromise = null;
let phonesPromise = null;
let bankAccountsPromise = null;
let addressesPromise = null;

const ensureUserCacheValid = (userKey) => {
  const token = localStorage.getItem('access_token');
  if (!token) {
    currentCacheUserKey = null;
    cachedIdentity = null;
    identityPromise = null;
    cachedEmails = null;
    cachedPhones = null;
    cachedAddresses = null;
    cachedBankAccounts = null;
    cachedBankAccountsPagination = null;
    emailsPromise = null;
    phonesPromise = null;
    bankAccountsPromise = null;
    addressesPromise = null;
    return;
  }
  if (!userKey) return;
  if (currentCacheUserKey && currentCacheUserKey !== userKey) {
    cachedIdentity = null;
    identityPromise = null;
    cachedEmails = null;
    cachedPhones = null;
    cachedAddresses = null;
    cachedBankAccounts = null;
    cachedBankAccountsPagination = null;
    emailsPromise = null;
    phonesPromise = null;
    bankAccountsPromise = null;
    addressesPromise = null;
  }
  currentCacheUserKey = userKey;
};

const fetchIdentityOnce = async () => {
  if (cachedIdentity) return cachedIdentity;
  if (!identityPromise) {
    identityPromise = api.get('/users/me/identity')
      .then((res) => {
        cachedIdentity = res.data?.identity || null;
        return cachedIdentity;
      })
      .finally(() => {
        identityPromise = null;
      });
  }
  return identityPromise;
};

export default function ProfileManager() {
  const auth = useAuth();
  const currentUser = auth?.user || auth?.getActiveUser?.();
  const currentUserId = currentUser?.sso_id || currentUser?.id || currentUser?.username || null;
  ensureUserCacheValid(currentUserId);

  // 1) Individual identity fields (initialized from cachedIdentity if available, falling back to currentUser)
  const [designation, setDesignation] = useState(() => cachedIdentity?.designation || currentUser?.designation || '');
  const [firstName, setFirstName] = useState(() => cachedIdentity?.first_name || currentUser?.first_name || '');
  const [middleName, setMiddleName] = useState(() => cachedIdentity?.middle_name || currentUser?.middle_name || '');
  const [lastName, setLastName] = useState(() => cachedIdentity?.last_name || currentUser?.last_name || '');
  const [callingName, setCallingName] = useState(() => cachedIdentity?.calling_name || currentUser?.calling_name || '');
  const [birthday, setBirthday] = useState(() => {
    const b = cachedIdentity?.birthday || currentUser?.birthday;
    return b ? (typeof b === 'string' ? b.substring(0, 10) : '') : '';
  });
  const [nic, setNic] = useState(() => cachedIdentity?.nic || currentUser?.nic || '');
  const [username, setUsername] = useState(() => cachedIdentity?.username || currentUser?.username || '');
  const [usernameChangeCount, setUsernameChangeCount] = useState(() => cachedIdentity?.username_change_count ?? currentUser?.username_change_count ?? 0);

  // Section loading indicators
  const [loadingIdentity, setLoadingIdentity] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Pagination states for subsections
  const [identityLoaded, setIdentityLoaded] = useState(false);
  const [emailsLoaded, setEmailsLoaded] = useState(false);
  const [phonesLoaded, setPhonesLoaded] = useState(false);
  const [addressesLoaded, setAddressesLoaded] = useState(false);
  const [refreshEmailsTrigger, setRefreshEmailsTrigger] = useState(0);
  const [refreshPhonesTrigger, setRefreshPhonesTrigger] = useState(0);

  // Backend designation options
  const [designations, setDesignations] = useState(() => cachedDesignations || []);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [designationsError, setDesignationsError] = useState(false);

  // Section-level edit mode flags and backups for cancel behavior
  const [isEditingIdentity, setIsEditingIdentity] = useState(false);
  const [isEditingEmails, setIsEditingEmails] = useState(false);
  const [isEditingPhones, setIsEditingPhones] = useState(false);
  const [isEditingLocations, setIsEditingLocations] = useState(false);

  const [identityBackup, setIdentityBackup] = useState(null);
  const [emailsBackup, setEmailsBackup] = useState(null);
  const [phonesBackup, setPhonesBackup] = useState(null);
  const [contactsBackup, setContactsBackup] = useState(null);
  const [locationsBackup, setLocationsBackup] = useState(null);

  // Baseline identity snapshot for diff comparison
  const [savedIdentity, setSavedIdentity] = useState(() => cachedIdentity || null);

  // Username validation & debounce check
  const [usernameAvailability, setUsernameAvailability] = useState({ checking: false, available: null, message: '' });

  // Confirmation modal state for one-time fields
  const [showIdentityConfirmModal, setShowIdentityConfirmModal] = useState(false);
  const [pendingDiff, setPendingDiff] = useState(null);
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);
  const [savingPhones, setSavingPhones] = useState(false);
  const [savingContacts, setSavingContacts] = useState(false);
  const [sectionError, setSectionError] = useState(null);

  // In-place Contact Verification Modal State
  const [verifyModal, setVerifyModal] = useState({
    isOpen: false,
    type: 'email',
    value: '',
    otp: '',
    devOtp: null,
    loading: false,
    error: null,
  });

  const effectiveIdentity = savedIdentity || cachedIdentity;
  const isBirthdayLocked = !!(effectiveIdentity?.birthday || currentUser?.birthday);
  const isNicLocked = !!(effectiveIdentity?.nic || currentUser?.nic);
  const isUsernameLocked = ((effectiveIdentity?.username_change_count ?? usernameChangeCount ?? currentUser?.username_change_count ?? 0) >= 1);

  const fetchEmails = async (page = 1) => {
    if (page === 1 && emailsPromise) {
      await emailsPromise;
      return;
    }
    const promise = (async () => {
      setLoadingEmails(true);
      try {
        const res = await api.get(`/users/me/emails?page=${page}&per_page=5`);
        const fetchedEmails = (res.data?.emails || []).map((e) => ({
          id: `email_${e.id || genId()}`,
          address: e.address || e.value,
          isPrimary: !!e.is_primary,
          isVerified: !!e.is_verified,
          isNew: false,
        }));
        setEmails(fetchedEmails);
        if (res.data?.pagination) {
          setEmailsPagination(res.data.pagination);
        }
        cachedEmails = fetchedEmails;
      } catch (err) {
        console.warn('Failed to load emails:', err);
      } finally {
        setLoadingEmails(false);
        if (page === 1) emailsPromise = null;
      }
    })();
    if (page === 1) emailsPromise = promise;
    await promise;
  };

  const fetchPhones = async (page = 1) => {
    if (page === 1 && phonesPromise) {
      await phonesPromise;
      return;
    }
    const promise = (async () => {
      setLoadingPhones(true);
      try {
        const res = await api.get(`/users/me/phones?page=${page}&per_page=5`);
        const fetchedPhones = (res.data?.phones || []).map((p) => {
          const { countryCode, number } = parsePhoneComponents(p.value || p.number, p.country_code, p.number);
          return {
            id: `phone_${p.id || genId()}`,
            countryCode,
            number,
            value: p.value || p.number,
            isPrimary: !!p.is_primary,
            isVerified: !!p.is_verified,
            isNew: false,
          };
        });
        setPhones(fetchedPhones);
        if (res.data?.pagination) {
          setPhonesPagination(res.data.pagination);
        }
        cachedPhones = fetchedPhones;
      } catch (err) {
        console.warn('Failed to load phones:', err);
      } finally {
        setLoadingPhones(false);
        if (page === 1) phonesPromise = null;
      }
    })();
    if (page === 1) phonesPromise = promise;
    await promise;
  };

  const fetchBankAccounts = async (page = 1) => {
    if (page === 1 && bankAccountsPromise) {
      await bankAccountsPromise;
      return;
    }
    const promise = (async () => {
      try {
        const res = await api.get(`/users/me/bank-accounts?page=${page}&per_page=5`);
        cachedBankAccounts = res.data.bank_accounts || [];
        if (res.data.pagination) {
          cachedBankAccountsPagination = res.data.pagination;
        }
        setBanks(cachedBankAccounts);
      } catch (err) {
        console.warn('Failed to load bank accounts:', err);
        setBanks([]);
      } finally {
        if (page === 1) bankAccountsPromise = null;
      }
    })();
    if (page === 1) bankAccountsPromise = promise;
    await promise;
  };


  const fetchAddresses = async (page = 1) => {
    if (page === 1 && addressesPromise) {
      await addressesPromise;
      return;
    }
    const promise = (async () => {
      setLoadingAddresses(true);
      try {
        const res = await api.get(`/users/me/addresses?page=${page}&per_page=5`);
        const fetchedAddresses = (res.data?.addresses || []).map((a) => ({
          id: a.id || genId(),
          countryCode: a.country_code || 'US',
          addressLine1: a.address_line_1 || a.addressLine1 || '',
          addressLine2: a.address_line_2 || a.addressLine2 || '',
          city: a.city || '',
          stateProvinceRegion: a.state_province_region || a.stateProvinceRegion || '',
          postalCode: a.postal_code || a.postalCode || '',
          usages: Array.isArray(a.usages) ? a.usages : [],
          isPrimary: !!a.is_primary,
          verificationStatus: a.verification_status || a.verificationStatus || 'pending',
        }));
        setAddresses(fetchedAddresses);
        if (res.data?.pagination) {
          setAddressesPagination(res.data.pagination);
        }
        cachedAddresses = fetchedAddresses;
      } catch (err) {
        console.warn('Failed to load addresses:', err);
      } finally {
        setLoadingAddresses(false);
        if (page === 1) addressesPromise = null;
      }
    })();
    if (page === 1) addressesPromise = promise;
    await promise;
  };

  // Sequential section data fetching:
  // Step 1: Designations
  // Step 2: Personal Identity (/users/me/identity)
  // Step 3: Emails & Phones (/users/me/emails, /users/me/phones)
  // Step 4: Physical Addresses (/users/me/addresses)
  useEffect(() => {
    let isMounted = true;

    // GATING: Under no circumstances load section data unless the auth session is ready, verified, and free of errors
    if (!auth?.isSessionReady || auth?.loading || auth?.authError || !currentUser || !currentUserId || auth?.sessionExpired) {
      return;
    }

    setSectionError(null);
    setIdentityLoaded(false);
    setEmailsLoaded(false);
    setPhonesLoaded(false);
    setAddressesLoaded(false);
    ensureUserCacheValid(currentUserId);
    
    // Clear module cache to force re-fetch on navigation (mimic full reload behavior)
    // We explicitly DO NOT clear the *Promise variables here. If React StrictMode double-mounts,
    // the first mount will set the Promise, and the second mount will reuse it. The Promises 
    // clear themselves in their finally() blocks, so a real navigation later will fetch fresh data.
    cachedIdentity = null;
    cachedEmails = null;
    cachedPhones = null;
    cachedBankAccounts = null;
    cachedAddresses = null;

    // Populate or reset local state from the current active user / cache
    const base = cachedIdentity || currentUser;
    setSavedIdentity(cachedIdentity || null);
    setDesignation(base?.designation || '');
    setFirstName(base?.first_name || '');
    setMiddleName(base?.middle_name || '');
    setLastName(base?.last_name || '');
    setCallingName(base?.calling_name || '');
    setBirthday(base?.birthday ? (typeof base.birthday === 'string' ? base.birthday.substring(0, 10) : '') : '');
    setNic(base?.nic || '');
    setUsername(base?.username || '');
    setUsernameChangeCount(base?.username_change_count ?? 0);

    if (cachedDesignations && cachedDesignations.length > 0) {
      setDesignations(cachedDesignations);
    }

    if (cachedAddresses) {
      setAddresses((cachedAddresses || []).map((a) => ({
        id: a.id || genId(),
        countryCode: a.country_code || 'US',
        addressLine1: a.address_line_1 || '',
        addressLine2: a.address_line_2 || '',
        city: a.city || '',
        stateProvinceRegion: a.state_province_region || '',
        postalCode: a.postal_code || '',
        usages: Array.isArray(a.usages) ? a.usages : [],
        isPrimary: !!a.is_primary,
        verificationStatus: a.verification_status || 'pending',
      })));
    } else {
      setAddresses([]);
    }

    setBanks([]);

    if (cachedEmails && Array.isArray(cachedEmails) && cachedEmails.length > 0) {
      setEmails(cachedEmails);
    } else {
      const initEmails = [];
      if (currentUser?.email) {
        initEmails.push({ id: 'email_primary', address: currentUser.email, isPrimary: true, isVerified: false, isNew: false });
      }
      setEmails(initEmails);
    }

    if (cachedPhones && Array.isArray(cachedPhones) && cachedPhones.length > 0) {
      setPhones(cachedPhones);
    } else {
      const initPhones = [];
      if (currentUser?.phone) {
        const { countryCode, number } = parsePhoneComponents(currentUser.phone);
        initPhones.push({ id: 'phone_primary', countryCode, number, value: currentUser.phone, isPrimary: true, isVerified: false, isNew: false });
      }
      setPhones(initPhones);
    }

    const loadSectionsSequentially = async () => {
      // Step 1: Designations (cached so only 1 request ever fires)
      if (!cachedDesignations || cachedDesignations.length === 0) {
        setLoadingDesignations(true);
      }
      try {
        const desigs = await fetchDesignationsOnce();
        if (isMounted && Array.isArray(desigs)) {
          setDesignations(desigs);
          setDesignationsError(false);
        }
      } catch (err) {
        console.warn('Failed to load designations:', err?.message || err);
        if (isMounted) setDesignationsError(true);
      } finally {
        if (isMounted) setLoadingDesignations(false);
      }

      // Step 2: Dedicated Personal Identity Section API
      if (!cachedIdentity) {
        setLoadingIdentity(true);
      }
      try {
        const idData = await fetchIdentityOnce();
        if (isMounted && idData) {
          setSavedIdentity(idData);
          setDesignation(idData.designation || '');
          setFirstName(idData.first_name || '');
          setMiddleName(idData.middle_name || '');
          setLastName(idData.last_name || '');
          setCallingName(idData.calling_name || '');
          setBirthday(idData.birthday ? (typeof idData.birthday === 'string' ? idData.birthday.substring(0, 10) : '') : '');
          setNic(idData.nic || '');
          setUsername(idData.username || '');
          setIsBiometricLinked(!!idData.has_biometric);
          setBiometricLinkedTime(idData.biometric_linked_time || null);
          setUsernameChangeCount(idData.username_change_count ?? 0);
          setIdentityLoaded(true);
        }
      } catch (err) {
        console.warn('Failed to load identity section:', err?.message || err);
        if (isMounted) {
          const status = err?.response?.status;
          setSectionError({
            title: status === 503 ? 'Database Service Unavailable' : 'Identity Loading Failed',
            message: err?.response?.data?.message || (err?.message === 'Network Error' ? 'Unable to reach server' : err?.message) || 'Failed to load personal identity data.',
            status,
            retryFn: () => {
              setSectionError(null);
              loadSectionsSequentially();
            },
          });
        }
        // CRITICAL: Abort subsequent requests in sequence if identity fails
        return;
      } finally {
        if (isMounted) setLoadingIdentity(false);
      }

      
    };

    loadSectionsSequentially();

    return () => {
      isMounted = false;
    };
  }, [currentUserId, auth?.loading, auth?.sessionExpired, auth?.isSessionReady, auth?.authError]);

  // Immediately dismiss any section error if auth enters an error state or session is not ready
  useEffect(() => {
    if (auth?.authError || !auth?.isSessionReady) {
      setSectionError(null);
    }
  }, [auth?.authError, auth?.isSessionReady]);

  // Debounced username availability check
  useEffect(() => {
    if (isUsernameLocked || !isEditingIdentity) {
      setUsernameAvailability({ checking: false, available: null, message: '' });
      return;
    }
    const trimmed = (username || '').trim();
    if (!trimmed || trimmed === (currentUser?.username || '')) {
      setUsernameAvailability({ checking: false, available: null, message: '' });
      return;
    }

    setUsernameAvailability({ checking: true, available: null, message: 'Checking availability...' });
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/check-username?username=${encodeURIComponent(trimmed)}`);
        setUsernameAvailability({
          checking: false,
          available: !!res.data?.available,
          message: res.data?.message || '',
        });
      } catch (err) {
        setUsernameAvailability({
          checking: false,
          available: false,
          message: err.response?.data?.message || 'Error checking username.',
        });
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [username, isUsernameLocked, isEditingIdentity, currentUser?.username]);

  // 2) Dynamic array states strictly aligned with user_emails and user_phones tables
  const [emails, setEmails] = useState(() => {
    if (cachedEmails && Array.isArray(cachedEmails) && cachedEmails.length > 0) {
      return cachedEmails;
    }
    if (currentUser?.email) {
      return [{ id: 'email_primary', address: currentUser.email, isPrimary: true, isVerified: false }];
    }
    return [];
  });

  const [phones, setPhones] = useState(() => {
    if (cachedPhones && Array.isArray(cachedPhones) && cachedPhones.length > 0) {
      return cachedPhones;
    }
    if (currentUser?.phone) {
      const { countryCode, number } = parsePhoneComponents(currentUser.phone);
      return [{ id: 'phone_primary', countryCode, number, value: currentUser.phone, isPrimary: true, isVerified: false, isNew: false }];
    }
    return [];
  });

  const [addresses, setAddresses] = useState(() => {
    if (cachedAddresses && Array.isArray(cachedAddresses) && cachedAddresses.length > 0) {
      return cachedAddresses.map((a) => ({
        id: a.id || genId(),
        countryCode: a.country_code || 'US',
        addressLine1: a.address_line_1 || '',
        addressLine2: a.address_line_2 || '',
        city: a.city || '',
        stateProvinceRegion: a.state_province_region || '',
        postalCode: a.postal_code || '',
        usages: Array.isArray(a.usages) ? a.usages : [],
        isPrimary: !!a.is_primary,
        verificationStatus: a.verification_status || 'pending',
      }));
    }
    return [];
  });

  const [banks, setBanks] = useState(() => {
    return cachedBankAccounts;
  });

  // Change detection for Personal Identity section
  const hasIdentityChanges = useMemo(() => {
    const base = savedIdentity || cachedIdentity || currentUser;
    const origFirst = (base?.first_name || '').trim();
    const origLast = (base?.last_name || '').trim();
    const origMiddle = (base?.middle_name || '').trim();
    const origCalling = (base?.calling_name || '').trim();
    const origDesig = base?.designation || '';
    const origBday = base?.birthday ? (typeof base.birthday === 'string' ? base.birthday.substring(0, 10) : '') : '';
    const origNic = (base?.nic || '').trim();
    const origUsername = (base?.username || '').trim();

    if (firstName.trim() !== origFirst) return true;
    if (lastName.trim() !== origLast) return true;
    if (middleName.trim() !== origMiddle) return true;
    if (callingName.trim() !== origCalling) return true;
    if (designation !== origDesig) return true;
    if (!isBirthdayLocked && birthday && birthday !== origBday) return true;
    if (!isNicLocked && nic.trim() && nic.trim() !== origNic) return true;
    if (!isUsernameLocked && username.trim() && username.trim() !== origUsername) return true;

    return false;
  }, [
    savedIdentity, cachedIdentity, currentUser,
    firstName, lastName, middleName, callingName, designation,
    isBirthdayLocked, birthday, isNicLocked, nic, isUsernameLocked, username
  ]);

  // Change detection for Email Addresses subsection
  const hasEmailChanges = useMemo(() => {
    if (!emailsBackup) return false;
    const currEmails = emails.filter((e) => (e.address && e.address.trim() !== '') || !e.isNew);
    const prevEmails = (emailsBackup || []).filter((e) => (e.address && e.address.trim() !== '') || !e.isNew);
    if (currEmails.length !== prevEmails.length) return true;
    for (let i = 0; i < currEmails.length; i++) {
      if ((currEmails[i].address || '').trim().toLowerCase() !== (prevEmails[i].address || '').trim().toLowerCase()) return true;
      if (!!currEmails[i].isPrimary !== !!prevEmails[i].isPrimary) return true;
    }
    return false;
  }, [emails, emailsBackup]);

  // Change detection for Phone Numbers subsection
  const hasPhoneChanges = useMemo(() => {
    if (!phonesBackup) return false;
    const currPhones = phones.filter((p) => (p.number && p.number.trim() !== '') || !p.isNew);
    const prevPhones = (phonesBackup || []).filter((p) => (p.number && p.number.trim() !== '') || !p.isNew);
    if (currPhones.length !== prevPhones.length) return true;
    for (let i = 0; i < currPhones.length; i++) {
      const cCode = (currPhones[i].countryCode || '+1').trim();
      const pCode = (prevPhones[i].countryCode || '+1').trim();
      const cNum = (currPhones[i].number || '').replace(/[^\d]/g, '');
      const pNum = (prevPhones[i].number || '').replace(/[^\d]/g, '');
      if (cCode !== pCode || cNum !== pNum) return true;
      if (!!currPhones[i].isPrimary !== !!prevPhones[i].isPrimary) return true;
    }
    return false;
  }, [phones, phonesBackup]);

  // Change detection for Addresses subsection
  const hasAddressesChanges = useMemo(() => {
    if (!locationsBackup || !locationsBackup.addresses) return false;
    const curr = addresses.filter((a) => (a.addressLine1 && a.addressLine1.trim() !== '') || !a.isNew);
    const prev = (locationsBackup.addresses || []).filter((a) => (a.addressLine1 && a.addressLine1.trim() !== '') || !a.isNew);
    if (curr.length !== prev.length) return true;
    for (let i = 0; i < curr.length; i++) {
      const c = curr[i];
      const p = prev[i];
      if ((c.addressLine1 || '').trim() !== (p.addressLine1 || '').trim()) return true;
      if ((c.addressLine2 || '').trim() !== (p.addressLine2 || '').trim()) return true;
      if ((c.city || '').trim() !== (p.city || '').trim()) return true;
      if ((c.stateProvinceRegion || '').trim() !== (p.stateProvinceRegion || '').trim()) return true;
      if ((c.postalCode || '').trim() !== (p.postalCode || '').trim()) return true;
      if ((c.countryCode || '').trim() !== (p.countryCode || '').trim()) return true;
      if (!!c.isPrimary !== !!p.isPrimary) return true;
    }
    return false;
  }, [addresses, locationsBackup]);

  // Section edit handlers
  const startIdentityEdit = () => {
    const base = savedIdentity || cachedIdentity || currentUser;
    const initialDesignation = designation || base?.designation || '';
    const initialFirst = firstName || base?.first_name || '';
    const initialMiddle = middleName || base?.middle_name || '';
    const initialLast = lastName || base?.last_name || '';
    const initialCalling = callingName || base?.calling_name || '';
    const initialBirthday = birthday || (base?.birthday ? (typeof base.birthday === 'string' ? base.birthday.substring(0, 10) : '') : '');
    const initialNic = nic || base?.nic || '';
    const initialUsername = username || base?.username || '';

    setIdentityBackup({
      designation: initialDesignation,
      firstName: initialFirst,
      middleName: initialMiddle,
      lastName: initialLast,
      callingName: initialCalling,
      birthday: initialBirthday,
      nic: initialNic,
      username: initialUsername,
    });

    if (!designation && initialDesignation) setDesignation(initialDesignation);
    if (!firstName && initialFirst) setFirstName(initialFirst);
    if (!middleName && initialMiddle) setMiddleName(initialMiddle);
    if (!lastName && initialLast) setLastName(initialLast);
    if (!callingName && initialCalling) setCallingName(initialCalling);
    if (!birthday && initialBirthday) setBirthday(initialBirthday);
    if (!nic && initialNic) setNic(initialNic);
    if (!username && initialUsername) setUsername(initialUsername);

    setIsEditingIdentity(true);
  };

  const cancelIdentityEdit = () => {
    if (identityBackup) {
      setDesignation(identityBackup.designation);
      setFirstName(identityBackup.firstName);
      setMiddleName(identityBackup.middleName);
      setLastName(identityBackup.lastName);
      setCallingName(identityBackup.callingName);
      setBirthday(identityBackup.birthday);
      setNic(identityBackup.nic);
      setUsername(identityBackup.username);
    }
    setIdentityBackup(null);
    setIsEditingIdentity(false);
    setUsernameAvailability({ checking: false, available: null, message: '' });
  };

  const handleSaveIdentityClick = () => {
    const diff = {};
    const base = savedIdentity || cachedIdentity || currentUser;
    const origFirst = base?.first_name || '';
    const origLast = base?.last_name || '';
    const origMiddle = base?.middle_name || '';
    const origCalling = base?.calling_name || '';
    const origDesig = base?.designation || '';
    const origBday = base?.birthday ? (typeof base.birthday === 'string' ? base.birthday.substring(0, 10) : '') : '';
    const origNic = base?.nic || '';
    const origUsername = base?.username || '';

    if (firstName.trim() !== origFirst) diff.first_name = firstName.trim();
    if (lastName.trim() !== origLast) diff.last_name = lastName.trim();
    if (middleName.trim() !== origMiddle) diff.middle_name = middleName.trim();
    if (callingName.trim() !== origCalling) diff.calling_name = callingName.trim();
    if (designation !== origDesig) diff.designation = designation;

    if (!isBirthdayLocked && birthday && birthday !== origBday) {
      diff.birthday = birthday;
    }
    if (!isNicLocked && nic.trim() && nic.trim() !== origNic) {
      diff.nic = nic.trim();
    }
    if (!isUsernameLocked && username.trim() && username.trim() !== origUsername) {
      if (usernameAvailability.available === false) {
        showNotice(usernameAvailability.message || 'Please choose an available username before saving.', 'error');
        return;
      }
      diff.username = username.trim();
    }

    if (Object.keys(diff).length === 0) {
      showNotice('No changes detected to save.', 'info');
      setIsEditingIdentity(false);
      return;
    }

    const permanentFields = [];
    if (diff.birthday) permanentFields.push('Birthday');
    if (diff.nic) permanentFields.push('Identity Card (NIC)');
    if (diff.username) permanentFields.push('Username');

    if (permanentFields.length > 0) {
      setPendingDiff({ diff, permanentFields });
      setShowIdentityConfirmModal(true);
    } else {
      executeIdentitySave(diff);
    }
  };

  const executeIdentitySave = async (diff) => {
    setSavingIdentity(true);
    try {
      const res = await api.put('/users/me/identity', diff);
      const updatedIdentity = res.data?.identity;
      if (updatedIdentity) {
        cachedIdentity = { ...(cachedIdentity || {}), ...updatedIdentity };
        setSavedIdentity((prev) => ({ ...prev, ...updatedIdentity }));
        if (updatedIdentity.username_change_count !== undefined) {
          setUsernameChangeCount(updatedIdentity.username_change_count);
        }
        if (auth?.setSession && auth?.user) {
          const userPatch = {};
          if (updatedIdentity.first_name !== undefined) userPatch.first_name = updatedIdentity.first_name;
          if (updatedIdentity.last_name !== undefined) userPatch.last_name = updatedIdentity.last_name;
          if (updatedIdentity.username !== undefined) userPatch.username = updatedIdentity.username;
          if (updatedIdentity.designation !== undefined) userPatch.designation = updatedIdentity.designation;
          const token = localStorage.getItem('access_token');
          const refreshTok = localStorage.getItem('refresh_token');
          auth.setSession(token, refreshTok, { ...auth.user, ...userPatch });
        }
      }
      showNotice(res.data?.message || 'Personal identity updated successfully!', 'success');
      setIdentityBackup(null);
      setIsEditingIdentity(false);
      setShowIdentityConfirmModal(false);
      setPendingDiff(null);
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.username?.[0] ||
        err?.response?.data?.errors?.nic?.[0] ||
        err?.response?.data?.errors?.birthday?.[0] ||
        err?.response?.data?.message ||
        'Failed to save personal identity.';
      showNotice(errorMsg, 'error');
    } finally {
      setSavingIdentity(false);
    }
  };

  // Subsection handlers for Email Addresses
  const startEmailsEdit = () => {
    setEmailsBackup(JSON.parse(JSON.stringify(emails)));
    setIsEditingEmails(true);
  };
  const cancelEmailsEdit = () => {
    if (emailsBackup) {
      setEmails(emailsBackup);
    }
    setEmailsBackup(null);
    setIsEditingEmails(false);
  };
  const saveEmailsEdit = async () => {
    const validEmails = emails.filter((e) => e.address && e.address.trim() !== '');
    if (validEmails.length === 0) {
      showNotice('At least one email address is required.', 'error');
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    for (const e of validEmails) {
      const addr = e.address.trim();
      if (!emailRegex.test(addr)) {
        showNotice(`Invalid email format: "${addr}".`, 'error');
        return;
      }
    }

    const emailLowerList = validEmails.map((e) => e.address.trim().toLowerCase());
    if (new Set(emailLowerList).size !== emailLowerList.length) {
      showNotice('Duplicate email addresses are not allowed.', 'error');
      return;
    }

    if (validEmails.length > 0 && !validEmails.some((e) => e.isPrimary)) {
      validEmails[0].isPrimary = true;
    }

    setEmails(validEmails);
    setSavingEmails(true);
    try {
      const payload = {
        emails: validEmails.map((e) => ({
          address: e.address.trim(),
          isPrimary: !!e.isPrimary,
        })),
      };

      const res = await api.put('/users/me/identities', payload);
      const rawEmails = res.data?.emails;

      if (Array.isArray(rawEmails)) {
        const parsedEmails = rawEmails
          .map((i) => ({
            id: `email_${i.id || genId()}`,
            address: i.value,
            isPrimary: !!i.is_primary,
            isVerified: !!i.is_verified,
            isNew: false,
          }));
        cachedEmails = parsedEmails;
        setEmails(parsedEmails);

        if (auth?.setSession && auth?.user) {
          const primaryEmailObj = parsedEmails.find((e) => e.isPrimary);
          if (primaryEmailObj?.address) {
            const token = localStorage.getItem('access_token');
            const refreshTok = localStorage.getItem('refresh_token');
            auth.setSession(token, refreshTok, { ...auth.user, email: primaryEmailObj.address });
          }
        }
      }

      setEmailsBackup(null);
      setIsEditingEmails(false);
      showNotice(res.data?.message || 'Email addresses updated successfully', 'success');
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.emails?.[0] ||
        err?.response?.data?.message ||
        'Failed to save email addresses.';
      showNotice(errorMsg, 'error');
    } finally {
      setSavingEmails(false);
    }
  };

  // Subsection handlers for Phone Numbers
  const startPhonesEdit = () => {
    setPhonesBackup(JSON.parse(JSON.stringify(phones)));
    setIsEditingPhones(true);
  };
  const cancelPhonesEdit = () => {
    if (phonesBackup) {
      setPhones(phonesBackup);
    }
    setPhonesBackup(null);
    setIsEditingPhones(false);
  };
  const savePhonesEdit = async () => {
    const cleanPhones = phones.filter((p) => p.number && p.number.trim() !== '');

    for (const p of cleanPhones) {
      const formatted = validateAndFormatPhone(p.number, p.countryCode || '+1');
      if (!formatted) {
        showNotice(`Invalid phone number: "${p.number}". Please enter a valid international phone number for the selected country.`, 'error');
        return;
      }
    }

    const phoneList = cleanPhones.map((p) => validateAndFormatPhone(p.number, p.countryCode || '+1'));
    if (new Set(phoneList).size !== phoneList.length) {
      showNotice('Duplicate phone numbers are not allowed.', 'error');
      return;
    }

    if (cleanPhones.length > 0 && !cleanPhones.some((p) => p.isPrimary)) {
      cleanPhones[0].isPrimary = true;
    }

    setPhones(cleanPhones);
    setSavingPhones(true);
    try {
      const payload = {
        phones: cleanPhones.map((p) => ({
          country_code: p.countryCode || '+1',
          number: (p.number || '').replace(/[^\d]/g, ''),
          isPrimary: !!p.isPrimary,
        })),
      };

      const res = await api.put('/users/me/identities', payload);
      const rawPhones = res.data?.phones;

      if (Array.isArray(rawPhones)) {
        const parsedPhones = rawPhones
          .map((i) => {
            const { countryCode, number } = parsePhoneComponents(i.value, i.country_code, i.number);
            return {
              id: `phone_${i.id || genId()}`,
              countryCode,
              number,
              value: i.value,
              isPrimary: !!i.is_primary,
              isVerified: !!i.is_verified,
              isNew: false,
            };
          });
        cachedPhones = parsedPhones;
        setPhones(parsedPhones);

        if (auth?.setSession && auth?.user) {
          const primaryPhoneObj = parsedPhones.find((p) => p.isPrimary);
          const userPatch = {};
          if (primaryPhoneObj?.value) userPatch.phone = primaryPhoneObj.value;
          else if (primaryPhoneObj?.countryCode && primaryPhoneObj?.number) {
            userPatch.phone = primaryPhoneObj.countryCode + primaryPhoneObj.number;
          }
          if (Object.keys(userPatch).length > 0) {
            const token = localStorage.getItem('access_token');
            const refreshTok = localStorage.getItem('refresh_token');
            auth.setSession(token, refreshTok, { ...auth.user, ...userPatch });
          }
        }
      }

      setPhonesBackup(null);
      setIsEditingPhones(false);
      showNotice(res.data?.message || 'Phone numbers updated successfully', 'success');
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.phones?.[0] ||
        err?.response?.data?.message ||
        'Failed to save phone numbers.';
      showNotice(errorMsg, 'error');
    } finally {
      setSavingPhones(false);
    }
  };

  const startLocationsEdit = () => {
    setLocationsBackup({ addresses: JSON.parse(JSON.stringify(addresses)), banks: JSON.parse(JSON.stringify(banks)) });
    setIsEditingLocations(true);
  };
  const cancelLocationsEdit = () => {
    if (locationsBackup) {
      setAddresses(locationsBackup.addresses);
      setBanks(locationsBackup.banks);
    }
    setLocationsBackup(null);
    setIsEditingLocations(false);
  };
  const saveLocationsEdit = () => {
    setLocationsBackup(null);
    setIsEditingLocations(false);
    showNotice('Location & bank changes saved', 'success');
  };

  // 3) Add/Delete handlers for dynamic rows
  const addEmailRow = () => setEmails((s) => {
    // The very first contact record added naturally becomes the primary one
    const hasExisting = s.some((r) => !r.isNew || (r.address && r.address.trim() !== ''));
    const isPrimary = !hasExisting;
    return [...s, { id: genId(), address: '', isPrimary, isNew: true }];
  });
  const deleteEmailRow = (id) => {
    const target = emails.find((r) => r.id === id);
    if (!target) return;

    const othersWithAddress = emails.filter((r) => r.id !== id && r.address && r.address.trim() !== '');

    // Verified email deletion rules
    if (target.isVerified) {
      if (othersWithAddress.length === 0) {
        showNotice('To delete a verified email, a replacement email must be available.', 'warning');
        return;
      }
      if (target.isPrimary) {
        showNotice('Please set a replacement email as primary before deleting this verified email.', 'warning');
        return;
      }
    }

    // Prevent removing the last active email unless a replacement exists
    if (target.address && target.address.trim() !== '' && othersWithAddress.length === 0) {
      showNotice('At least one email address is required.', 'warning');
      return;
    }

    setEmails((s) => {
      const remaining = s.filter((r) => r.id !== id);
      if (target?.isPrimary && remaining.length > 0) {
        const firstSaved = remaining.find((r) => !r.isNew) || remaining[0];
        return remaining.map((r) => ({ ...r, isPrimary: r.id === firstSaved.id }));
      }
      return remaining;
    });
  };
  const updateEmail = (id, patch) => setEmails((s) => s.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const setPrimaryEmail = (id) => setEmails((s) => {
    const target = s.find((r) => r.id === id);
    // Disallow marking empty contact rows as primary at creation time
    if (!target || (!target.address?.trim() && !target.isPrimary)) {
      return s;
    }
    return s.map((r) => ({ ...r, isPrimary: r.id === id }));
  });

  const addPhoneRow = async () => {
    const loc = await detectUserLocationData();
    setPhones((s) => {
      // The very first contact record added naturally becomes the primary one
      const hasExisting = s.some((r) => !r.isNew || (r.number && r.number.trim() !== ''));
      const isPrimary = !hasExisting;
      return [...s, { id: genId(), countryCode: loc.dialCode, number: '', isPrimary, isVerified: false, isNew: true }];
    });
  };
  const deletePhoneRow = (id) => {
    const target = phones.find((r) => r.id === id);
    if (!target) return;

    const othersWithNumber = phones.filter((r) => r.id !== id && r.number && r.number.trim() !== '');

    // Verified phone deletion rules
    if (target.isVerified) {
      if (othersWithNumber.length === 0) {
        showNotice('To delete a verified phone number, a replacement phone number must be available.', 'warning');
        return;
      }
      if (target.isPrimary) {
        showNotice('Please set a replacement phone number as primary before deleting this verified phone number.', 'warning');
        return;
      }
    }

    setPhones((s) => {
      const remaining = s.filter((r) => r.id !== id);
      if (target?.isPrimary && remaining.length > 0) {
        const firstSaved = remaining.find((r) => !r.isNew) || remaining[0];
        return remaining.map((r) => ({ ...r, isPrimary: r.id === firstSaved.id }));
      }
      return remaining;
    });
  };
  const updatePhone = (id, patch) => setPhones((s) => s.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const setPrimaryPhone = (id) => setPhones((s) => {
    const target = s.find((r) => r.id === id);
    // Disallow marking empty contact rows as primary at creation time
    if (!target || (!target.number?.trim() && !target.isPrimary)) {
      return s;
    }
    return s.map((r) => ({ ...r, isPrimary: r.id === id }));
  });

  // In-place Contact Verification Handlers
  const handleInitiateVerification = async (type, value) => {
    try {
      setVerifyModal({
        isOpen: true,
        type,
        value,
        otp: '',
        devOtp: null,
        loading: true,
        error: null,
      });

      const res = await api.post('/users/me/identities/request-otp', { type, value });
      setVerifyModal((prev) => ({
        ...prev,
        loading: false,
        devOtp: res.data?.otp || null,
      }));
      showNotice(res.data?.message || 'Verification code sent.', 'info');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to send verification code.';
      setVerifyModal((prev) => ({
        ...prev,
        loading: false,
        error: msg,
      }));
      showNotice(msg, 'error');
    }
  };

  const handleConfirmVerification = async () => {
    if (!verifyModal.otp || verifyModal.otp.trim().length < 4) {
      setVerifyModal((prev) => ({ ...prev, error: 'Please enter the verification code.' }));
      return;
    }

    setVerifyModal((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await api.post('/users/me/identities/verify-otp', {
        type: verifyModal.type,
        value: verifyModal.value,
        otp: verifyModal.otp.trim(),
      });

      if (verifyModal.type === 'email') {
        setRefreshEmailsTrigger(prev => prev + 1);
      } else {
        setRefreshPhonesTrigger(prev => prev + 1);
      }

      setVerifyModal({
        isOpen: false,
        type: 'email',
        value: '',
        otp: '',
        devOtp: null,
        loading: false,
        error: null,
      });

      showNotice(res.data?.message || 'Contact verified successfully!', 'success');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Invalid or expired verification code.';
      setVerifyModal((prev) => ({ ...prev, loading: false, error: msg }));
    }
  };

  const handleResendOtp = async () => {
    setVerifyModal((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await api.post('/users/me/identities/request-otp', {
        type: verifyModal.type,
        value: verifyModal.value,
      });
      setVerifyModal((prev) => ({
        ...prev,
        loading: false,
        devOtp: res.data?.otp || null,
        error: null,
      }));
      showNotice(res.data?.message || 'A new verification code has been sent.', 'info');
    } catch (err) {
      setVerifyModal((prev) => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.message || 'Failed to resend code.',
      }));
    }
  };

  const addAddressRow = async () => {
    const loc = await detectUserLocationData();
    setAddresses((s) => {
      const isPrimary = s.length === 0;
      return [...s, {
        id: genId(),
        countryCode: loc.iso2,
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateProvinceRegion: '',
        postalCode: '',
        usages: [],
        isPrimary,
        verificationStatus: 'pending',
        isNew: true,
      }];
    });
  };
  const deleteAddressRow = (id) => setAddresses((s) => s.filter((r) => r.id !== id));
  const updateAddress = (id, patch) => setAddresses((s) => s.map((r) => {
    if (r.id !== id) return r;
    // If the row is verified, do not change verificationStatus; inputs should be disabled elsewhere
    const next = { ...r, ...patch };
    if (r.verificationStatus !== 'verified') {
      // Any edit sets verificationStatus to pending
      next.verificationStatus = 'pending';
    }
    return next;
  }));
  const setPrimaryAddress = (id) => setAddresses((s) => s.map((r) => ({ ...r, isPrimary: r.id === id })));

  const addBankRow = () => setBanks((s) => {
    const isPrimary = s.length === 0;
    return [...s, { id: genId(), bankName: '', branchCode: '', accountNumber: '', isPrimary, verificationStatus: 'pending' }];
  });
  const deleteBankRow = (id) => setBanks((s) => s.filter((r) => r.id !== id));
  const updateBank = (id, patch) => setBanks((s) => s.map((r) => {
    if (r.id !== id) return r;
    const next = { ...r, ...patch };
    if (r.verificationStatus !== 'verified') {
      next.verificationStatus = 'pending';
    }
    return next;
  }));
  const setPrimaryBank = (id) => setBanks((s) => s.map((r) => ({ ...r, isPrimary: r.id === id })));


  // Biometric security state
  const [isBiometricLinked, setIsBiometricLinked] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricLinkedTime, setBiometricLinkedTime] = useState(null);

  // Account lifecycle / modal / toast state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [notice, setNotice] = useState(null); // { type, message }

  // Avatar upload state
  const avatarInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
   // Operational Locks are supplied by the backend via the UserProfileContext hook.
   // Use live profile data when available; fall back to an empty array.
   // NOTE: Connected app enforcement is centralized in Overview.jsx
   const { profileData } = useUserProfileContext();
   const activeLocks = (profileData && (profileData.active_locks || profileData.activeLocks)) || [];

  const showNotice = (message, type = 'info') => {
    setNotice({ message, type });
    setTimeout(() => setNotice(null), 6000);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      showNotice('Please select a valid image file (JPEG, PNG, WebP, or GIF).', 'warning');
      return;
    }

    const maxSizeBytes = 2 * 1024 * 1024; // 2 MB max (PHP upload_max_filesize limit)

    // Show local preview immediately from the original file
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setAvatarUploading(true);

    // Attempt compression if the file exceeds the limit
    let fileToUpload = file;
    if (file.size > maxSizeBytes) {
      fileToUpload = await compressImageIfNeeded(file, maxSizeBytes);
      if (fileToUpload.size > maxSizeBytes) {
        showNotice('Image is too large even after compression. Please choose a smaller image (max 2 MB).', 'warning');
        setAvatarPreview(null);
        setAvatarUploading(false);
        if (avatarInputRef.current) avatarInputRef.current.value = '';
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('avatar', fileToUpload);

      // Do NOT hardcode Content-Type: multipart/form-data because axios / browser
      // must automatically generate the multipart boundary string.
      const res = await api.post('/users/me/avatar', formData);

      showNotice('Profile picture updated successfully!', 'success');

      // Use the fresh profile_image_url returned directly by the upload response.
      // No extra /me round-trip needed — the upload API already returns the new URL.
      const freshImageUrl = res?.data?.profile_image_url || null;
      const freshImagePath = res?.data?.profile_image_path || null;
      if (freshImageUrl && auth?.setSession) {
        const token = localStorage.getItem('access_token');
        const refreshTok = localStorage.getItem('refresh_token');
        // Merge updated avatar fields into the current user object in storage
        const currentUser = auth?.user || {};
        const updatedUser = {
          ...currentUser,
          profile_image_url: freshImageUrl,
          profile_image_path: freshImagePath,
          manually_changed: 'y',
        };
        auth.setSession(token, refreshTok, updatedUser);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.avatar?.[0] ||
        err?.response?.data?.message ||
        'Failed to upload profile picture.';
      showNotice(errorMsg, 'error');
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
      // Reset file input so the same file can be re-selected
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

   const openDeleteModal = () => {
     const deletionLock = activeLocks.find((lock) => lock.restrictions.includes('prevent_deletion'));
     if (deletionLock) {
       showNotice('Account deletion is restricted due to active operational holds.', 'warning');
       return;
     }
     setShowDeleteModal(true);
   };

  const closeDeleteModal = () => setShowDeleteModal(false);

  const confirmScheduledDeletion = async () => {
    setIsDeletingAccount(true);
    try {
      await api.delete('/users/me');
      setShowDeleteModal(false);
      showNotice('Account deletion scheduled. Logging out...', 'info');

      // Strictly log the user out of the application immediately
      if (auth?.logout) {
        await auth.logout(auth.activeIndex);
      }
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to request account deletion:', err);
      showNotice(err?.response?.data?.message || 'Failed to request account deletion. Please try again.', 'error');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const setupBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const resp = await api.post('/users/me/biometric/register/options', {}, { withCredentials: true });
      const options = resp.data;
      const attestationResponse = await startRegistration(options);
      await api.post('/users/me/biometric/register', attestationResponse, { withCredentials: true });
      
      setIsBiometricLinked(true);
      setBiometricLinkedTime(new Date().toISOString());
      showNotice('Biometric Passkey registered and securely bound to this device.', 'success');
      console.log('Biometric passkey registered successfully');
    } catch (err) {
      console.error('Biometric registration failed:', err);
      let errMsg = null;
      if (err?.name === 'NotAllowedError') {
        errMsg = 'Biometric registration cancelled or denied by user.';
      } else if (err?.response?.data?.message && err.response.data.message !== "null") {
        errMsg = err.response.data.message;
      } else if (err?.message && err.message !== "null") {
        errMsg = err.message;
      }
      
      showNotice(errMsg ? String(errMsg) : 'Failed to register biometric device. Check if your browser supports WebAuthn.', 'error');
    } finally {
      setBiometricLoading(false);
    }
  };

  const removeBiometricDevice = async () => {
    setBiometricLoading(true);
    try {
      await api.delete('/users/me/biometric');
      setIsBiometricLinked(false);
      setBiometricLinkedTime(null);
      showNotice('Biometric device removed from this account.', 'info');
      console.log('Biometric device removed');
    } catch (err) {
      console.error('Failed to remove biometric device:', err);
      showNotice('Failed to remove biometric device.', 'error');
    } finally {
      setBiometricLoading(false);
    }
  };


  const handleReloadDesignations = async () => {
    cachedDesignations = null;
    designationsPromise = null;
    setLoadingDesignations(true);
    setDesignationsError(false);
    try {
      const desigs = await fetchDesignationsOnce();
      if (Array.isArray(desigs)) {
        setDesignations(desigs);
      }
    } catch (err) {
      console.warn('Failed to reload designations:', err);
      setDesignationsError(true);
    } finally {
      setLoadingDesignations(false);
    }
  };

  const handleReloadIdentity = async () => {
    cachedIdentity = null;
    identityPromise = null;
    setLoadingIdentity(true);
    setSectionError(null);
    try {
      const idData = await fetchIdentityOnce();
      if (idData) {
        setSavedIdentity(idData);
        setDesignation(idData.designation || '');
        setFirstName(idData.first_name || '');
        setMiddleName(idData.middle_name || '');
        setLastName(idData.last_name || '');
        setCallingName(idData.calling_name || '');
        setBirthday(idData.birthday ? (typeof idData.birthday === 'string' ? idData.birthday.substring(0, 10) : '') : '');
        setNic(idData.nic || '');
        setUsername(idData.username || '');
        setUsernameChangeCount(idData.username_change_count ?? 0);
      }
    } catch (err) {
      console.warn('Failed to reload identity:', err);
    } finally {
      setLoadingIdentity(false);
    }
  };

  const handleReloadEmails = async () => {
    cachedEmails = null;
    emailsPromise = null;
    setLoadingEmails(true);
    setSectionError(null);
    try {
      await fetchEmails(emailsPagination?.current_page || 1);
    } catch (e) {
      setSectionError('Failed to reload emails');
    }
  };

  const handleReloadPhones = async () => {
    cachedPhones = null;
    phonesPromise = null;
    setLoadingPhones(true);
    setSectionError(null);
    try {
      await fetchPhones(phonesPagination?.current_page || 1);
    } catch (e) {
      setSectionError('Failed to reload phones');
    }
  };

  const handleReloadAddresses = async () => {
    cachedAddresses = null;
    addressesPromise = null;
    setLoadingAddresses(true);
    setSectionError(null);
    try {
      await fetchAddresses(addressesPagination?.current_page || 1);
    } catch (err) {
      console.warn('Failed to reload addresses:', err);
    } finally {
      setLoadingAddresses(false);
    }
  };

  return (
    <div className="space-y-6">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">Profile Manager</h1>
          <p className="text-slate-600 dark:text-slate-400">Manage your account identity, contact information, and financial details</p>
        </header>

        <div className="space-y-6">
          {/* Profile Picture Section */}
          <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Profile Picture</h2>
            <div className="flex items-center gap-6">
              {/* Avatar display */}
              <div className="relative group">
                <div className="h-24 w-24 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg overflow-hidden">
                  {(avatarPreview || currentUser?.profile_image_url) ? (
                    <img
                      src={avatarPreview || currentUser.profile_image_url}
                      alt="Profile"
                      className="h-full w-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                    />
                  ) : null}
                  <span
                    style={{ display: (avatarPreview || currentUser?.profile_image_url) ? 'none' : 'flex' }}
                    className="h-full w-full items-center justify-center"
                  >
                    {(() => {
                      const u = currentUser;
                      if (!u) return 'U';
                      const name = (u.first_name ? `${u.first_name} ${u.last_name || ''}` : u.name || u.username || '').trim();
                      const parts = name.split(' ').filter(Boolean);
                      return (parts.length >= 2 ? parts[0][0] + parts[1][0] : (parts[0] ? parts[0][0] : 'U')).toUpperCase();
                    })()}
                  </span>
                </div>
                {avatarUploading && (
                  <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
                    <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Upload controls */}
              <div className="flex-1">
                <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                  Upload a new profile picture. Accepted formats: JPEG, PNG, WebP, GIF. Max size: 2 MB.
                </p>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={avatarUploading}
                  onClick={() => avatarInputRef.current?.click()}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {avatarUploading ? 'Uploading...' : 'Change Photo'}
                </button>
                {currentUser?.manually_changed === 'y' && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">✓ Custom profile picture set</p>
                )}
              </div>
            </div>
          </section>

          {/* Block A: Personal Identity */}
          <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Personal Identity</h2>
                <button 
                  type="button" 
                  onClick={handleReloadIdentity} 
                  disabled={loadingIdentity || isEditingIdentity}
                  className={`p-1 text-slate-400 hover:text-indigo-600 transition-colors disabled:opacity-50 ${isEditingIdentity ? 'hidden' : ''}`}
                  title="Reload Identity Data"
                >
                  <svg className={`w-4 h-4 ${loadingIdentity ? 'animate-spin text-indigo-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              {!isEditingIdentity ? (
                <button type="button" onClick={startIdentityEdit} className="text-sm text-indigo-600 hover:underline font-medium">Edit</button>
              ) : (
                <div />
              )}
            </div>

            {!isEditingIdentity ? (
              <div className="space-y-2.5">
                <p className="text-sm text-slate-900 dark:text-white">
                  <span className="font-medium text-slate-500 dark:text-slate-400">Designation:</span>{' '}
                  {designation ? designation : <span className="text-slate-400 italic">Not set</span>}
                </p>
                <p className="text-sm text-slate-900 dark:text-white">
                  <span className="font-medium text-slate-500 dark:text-slate-400">Name:</span>{' '}
                  {[firstName, middleName, lastName].filter(Boolean).join(' ') || <span className="text-slate-400 italic">Not set</span>}
                </p>
                <p className="text-sm text-slate-900 dark:text-white">
                  <span className="font-medium text-slate-500 dark:text-slate-400">Calling Name:</span>{' '}
                  {callingName ? callingName : <span className="text-slate-400 italic">Not set</span>}
                </p>
                <p className="text-sm text-slate-900 dark:text-white">
                  <span className="font-medium text-slate-500 dark:text-slate-400">Birthday:</span>{' '}
                  {birthday ? birthday : <span className="text-slate-400 italic">Not selected</span>}
                </p>
                <p className="text-sm text-slate-900 dark:text-white">
                  <span className="font-medium text-slate-500 dark:text-slate-400">NIC:</span>{' '}
                  {nic ? nic : <span className="text-slate-400 italic">Not set</span>}
                </p>
                <p className="text-sm text-slate-900 dark:text-white mt-2">
                  <span className="font-medium text-slate-500 dark:text-slate-400">Username:</span>{' '}
                  {username ? (
                    <span className="font-mono text-indigo-600 dark:text-indigo-400">{username}</span>
                  ) : (
                    <span className="text-slate-400 italic">Not set</span>
                  )}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium">Designation</label>
                      {designationsError && (
                        <button
                          type="button"
                          onClick={handleReloadDesignations}
                          disabled={loadingDesignations}
                          className="text-xs text-rose-600 dark:text-rose-400 hover:underline flex items-center gap-1 disabled:opacity-50"
                        >
                          <svg className={`w-3 h-3 ${loadingDesignations ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Retry
                        </button>
                      )}
                      {!designationsError && loadingDesignations && (
                         <span className="text-xs text-indigo-600 flex items-center gap-1">
                           <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                           </svg>
                           Loading...
                         </span>
                      )}
                    </div>
                    <select
                      value={designation}
                      onChange={(e) => setDesignation(e.target.value)}
                      disabled={loadingDesignations || designationsError}
                      className={`w-full bg-white dark:bg-gray-800 border ${designationsError ? 'border-rose-300 dark:border-rose-700' : 'border-slate-300 dark:border-slate-700'} rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm`}
                    >
                      <option value="">-- Select Designation --</option>
                      {designations.map((d) => (
                        <option key={d.id || d.name} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                      {designation && !designations.some((d) => d.name === designation) && (
                        <option value={designation}>{designation}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium">Birthday</label>
                      {isBirthdayLocked && (
                        <span className="text-xs text-amber-500 font-medium">🔒 Permanent (Locked)</span>
                      )}
                    </div>
                    <input
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                      disabled={isBirthdayLocked}
                      value={birthday || ''}
                      onChange={(e) => setBirthday(e.target.value)}
                      className={`w-full bg-white dark:bg-gray-800 border ${
                        isBirthdayLocked
                          ? 'border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed bg-slate-100 dark:bg-gray-900/50'
                          : 'border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500'
                      } rounded-lg px-3 py-2 text-sm`}
                    />
                    {!isBirthdayLocked && !birthday && (
                      <p className="text-xs text-slate-400 mt-1">No birthday selected. You can select up to today.</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium mb-1">First Name</label>
                    <input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium mb-1">Middle Name</label>
                    <input
                      value={middleName}
                      onChange={(e) => setMiddleName(e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium mb-1">Last Name</label>
                    <input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium mb-1">Calling Name</label>
                    <input
                      value={callingName}
                      onChange={(e) => setCallingName(e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium">Identity Card (NIC)</label>
                      {isNicLocked && (
                        <span className="text-xs text-amber-500 font-medium">🔒 Permanent (Locked)</span>
                      )}
                    </div>
                    <input
                      value={nic}
                      disabled={isNicLocked}
                      placeholder={isNicLocked ? '' : 'e.g. 199512345678 or 951234567V'}
                      onChange={(e) => setNic(e.target.value)}
                      className={`w-full bg-white dark:bg-gray-800 border ${
                        isNicLocked
                          ? 'border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed bg-slate-100 dark:bg-gray-900/50'
                          : 'border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500'
                      } rounded-lg px-3 py-2 text-sm`}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm text-slate-700 dark:text-slate-300 font-medium">Username</label>
                    {isUsernameLocked ? (
                      <span className="text-xs text-amber-500 font-medium">🔒 Locked (Already set once)</span>
                    ) : (
                      <span className="text-xs text-slate-400">Can only be set once</span>
                    )}
                  </div>
                  <input
                    value={username}
                    disabled={isUsernameLocked}
                    placeholder="e.g. john_doe"
                    onChange={(e) => setUsername(e.target.value)}
                    className={`w-full bg-white dark:bg-gray-800 border ${
                      isUsernameLocked
                        ? 'border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed bg-slate-100 dark:bg-gray-900/50'
                        : usernameAvailability.available === true
                        ? 'border-emerald-500 focus:ring-emerald-500 text-slate-900 dark:text-white'
                        : usernameAvailability.available === false
                        ? 'border-rose-500 focus:ring-rose-500 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white focus:ring-indigo-500'
                    } rounded-lg px-3 py-2 text-sm`}
                  />
                  {!isUsernameLocked && usernameAvailability.message && (
                    <p
                      className={`text-xs mt-1 font-medium ${
                        usernameAvailability.checking
                          ? 'text-indigo-400'
                          : usernameAvailability.available === true
                          ? 'text-emerald-500'
                          : 'text-rose-500'
                      }`}
                    >
                      {usernameAvailability.checking && '⏳ '}
                      {usernameAvailability.available === true && '✓ '}
                      {usernameAvailability.available === false && '✗ '}
                      {usernameAvailability.message}
                    </p>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    disabled={savingIdentity}
                    onClick={cancelIdentityEdit}
                    className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={savingIdentity || !hasIdentityChanges || usernameAvailability.checking || usernameAvailability.available === false}
                    onClick={handleSaveIdentityClick}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {savingIdentity ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </section>

          {/* Block B: Contact Information */}
          <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl mb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Contact Information</h2>
              </div>
            </div>

            {/* Subsection: Email Addresses */}
            <EmailManager userId={currentUser?.id} shouldFetch={identityLoaded} onLoaded={() => setEmailsLoaded(true)} onRequestVerify={handleInitiateVerification} refreshTrigger={refreshEmailsTrigger} />
            
            <div className="w-full h-px bg-slate-200 dark:bg-slate-700 my-5"></div>
            
            {/* Subsection: Phone Numbers */}
            <PhoneManager userId={currentUser?.id} shouldFetch={emailsLoaded} onLoaded={() => setPhonesLoaded(true)} onRequestVerify={handleInitiateVerification} refreshTrigger={refreshPhonesTrigger} />
          </section>

          {/* Addresses & Financial Information Card */}
          <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl mb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Addresses & Financial Information</h2>
              </div>
            </div>
            
            <div className="w-full h-px bg-slate-200 dark:bg-slate-700 my-5"></div>
            
            {/* Subsection: Addresses */}
            <AddressManager userId={currentUser?.id} shouldFetch={phonesLoaded} onLoaded={() => setAddressesLoaded(true)} />
            
            <div className="w-full h-px bg-slate-200 dark:bg-slate-700 my-5"></div>
            
            <BankAccountsManager userId={currentUser?.id} shouldFetch={addressesLoaded} />
          </section>



            {/* Password Management */}
           <PasswordManager currentUser={currentUser} onSuccess={() => auth?.refreshCurrentUser?.()} />

           {/* Biometric Security & Passkeys Card */}
           <section className="bg-slate-50 dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-700 mb-6 shadow-xl">
             <div className="flex items-center justify-between mb-4">
               <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Biometric Authentication (Passkeys)</h2>
               <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium rounded border border-blue-300 dark:border-blue-700">
                 Secure your identity using system fingerprint readers, Windows Hello, or Face ID.
               </span>
             </div>

             {isBiometricLinked ? (
               <div className="space-y-4">
                 <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-300 dark:border-slate-700">
                   <div className="flex items-center gap-3">
                     <span className="text-2xl">🔒</span>
                     <div>
                       <p className="font-semibold text-slate-900 dark:text-white">TouchID / FaceID Active</p>
                       <p className="text-xs text-slate-600 dark:text-slate-400">Registered: {new Date(biometricLinkedTime).toLocaleString()}</p>
                     </div>
                   </div>
                   <button
                     type="button"
                     onClick={removeBiometricDevice}
                     className="px-3 py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded text-sm font-medium transition-colors"
                   >
                     Remove Device
                   </button>
                 </div>
               </div>
             ) : (
               <div className="space-y-4">
                 <p className="text-sm text-slate-700 dark:text-slate-300">
                   Enable biometric authentication for enhanced security and faster login. Your device's biometric data will be securely stored locally.
                 </p>
                 <button
                   type="button"
                   onClick={setupBiometricLogin}
                   disabled={biometricLoading}
                   className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                     biometricLoading
                       ? 'bg-indigo-600/50 dark:bg-indigo-600/50 text-slate-300 dark:text-slate-300 cursor-not-allowed'
                       : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                   }`}
                 >
                   {biometricLoading && (
                     <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                     </svg>
                   )}
                   {biometricLoading ? 'Waiting for security key or touch sensor activation...' : 'Setup Biometric Login'}
                 </button>
               </div>
             )}
           </section>

            {/* Account Security & Danger Zone */}
            <section className="border border-red-300 dark:border-red-900 bg-red-50 dark:bg-slate-950 p-6 rounded-xl mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Account Lifecycle & Data Privacy</h3>
              <p className="text-sm text-slate-700 dark:text-slate-400 mt-2">Manage long-running account actions and simulate account lifecycle behavior. Deleting an account will revoke access across connected systems.</p>

              {/* Deletion lock warning - high-visibility */}
              {activeLocks.some((lock) => lock.restrictions.includes('prevent_deletion')) && (
                <div className="mt-4 p-4 bg-red-100 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-700 rounded-lg">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🔒</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">Account Deletion Restricted</p>
                      <p className="text-sm text-red-700 dark:text-red-400">
                        Your identity profile has active administrative or operational holds from connected apps (e.g., <span className="font-semibold">{activeLocks.filter((l) => l.restrictions.includes('prevent_deletion')).map((l) => l.client_name).join(', ')}</span>). To protect ecosystem integrity, please resolve these external dependencies before closing your account.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-4">
                <div>
                  <p className="text-sm text-slate-700 dark:text-slate-300">Status: <span className="font-medium text-slate-900 dark:text-white">Active</span></p>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={openDeleteModal}
                    disabled={activeLocks.some((lock) => lock.restrictions.includes('prevent_deletion'))}
                    className={`px-4 py-2 rounded-md font-semibold transition-colors ${
                      activeLocks.some((lock) => lock.restrictions.includes('prevent_deletion'))
                        ? 'bg-red-700/50 dark:bg-red-900/50 text-red-900 dark:text-red-300 cursor-not-allowed'
                        : 'bg-red-700 hover:bg-red-600 dark:bg-red-900 dark:hover:bg-red-800 text-white'
                    }`}
                  >
                    Permanently Delete Account
                  </button>
                </div>
              </div>

            </section>

            {/* Page-level global actions removed; section-level edit/save controls are used instead */}
         </div>

         {/* Notice Toast */}
         {notice && (
           <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 w-auto max-w-md">
             <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-medium transition-all
               ${notice.type === 'success'
                 ? 'bg-emerald-50 dark:bg-emerald-950/80 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200'
                 : notice.type === 'error'
                 ? 'bg-red-50 dark:bg-red-950/80 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
                 : notice.type === 'warning'
                 ? 'bg-amber-50 dark:bg-amber-950/80 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200'
                 : 'bg-white dark:bg-slate-900/90 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white'}`}
             >
               {notice.message}
             </div>
           </div>
         )}

         {/* One-Time Fields Confirmation Modal */}
         {showIdentityConfirmModal && pendingDiff && (
           <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
             <div
               className="absolute inset-0 bg-black/60 backdrop-blur-sm"
               onClick={() => {
                 if (!savingIdentity) {
                   setShowIdentityConfirmModal(false);
                   setPendingDiff(null);
                 }
               }}
             />
             <div className="relative w-full max-w-md bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-700/80 rounded-xl p-6 shadow-2xl z-60 space-y-4">
               <div className="flex items-start gap-3">
                 <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center shrink-0">
                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                   </svg>
                 </div>
                 <div>
                   <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Attention: Permanent Details</h4>
                   <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Please review before saving</p>
                 </div>
               </div>

               <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-lg p-4 space-y-2 text-sm text-slate-800 dark:text-slate-200">
                 <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider">
                   The following field(s) can only be set once and CANNOT be modified later:
                 </p>
                 <ul className="list-disc list-inside space-y-1 text-sm font-medium text-slate-900 dark:text-white pl-1">
                   {pendingDiff.permanentFields.map((field) => (
                     <li key={field}>{field}</li>
                   ))}
                 </ul>
                 <p className="text-xs text-slate-600 dark:text-slate-400 pt-1">
                   Once saved, these values become permanent identity records for this account. Proceed only if verified.
                 </p>
               </div>

               <div className="flex justify-end gap-3 pt-2">
                 <button
                   type="button"
                   disabled={savingIdentity}
                   onClick={() => {
                     setShowIdentityConfirmModal(false);
                     setPendingDiff(null);
                   }}
                   className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white text-sm font-medium rounded-lg transition-colors"
                 >
                   Cancel
                 </button>
                 <button
                   type="button"
                   disabled={savingIdentity}
                   onClick={() => executeIdentitySave(pendingDiff.diff)}
                   className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                 >
                   {savingIdentity ? 'Saving...' : 'Confirm & Save'}
                 </button>
               </div>
             </div>
           </div>
         )}

          {/* Standard Account Deletion Confirmation Modal */}
          <ConfirmModal
            isOpen={showDeleteModal}
            title="Permanently Delete Account"
            message={
              <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                <p className="font-medium text-slate-800 dark:text-slate-200">
                  Are you sure you want to permanently delete your account? Please review the following conditions:
                </p>
                <ol className="list-decimal pl-5 space-y-2 text-xs">
                  <li>
                    <strong>14-Day Grace Period:</strong> The account will be permanently deleted after a 14-day delay.
                  </li>
                  <li>
                    <strong>Automatic Cancellation:</strong> If you log back in before the 14 days are up, the deletion process will be automatically canceled.
                  </li>
                  <li>
                    <strong>Clearance Check:</strong> Within this 14-day period, a clearance check will be sent to other systems you have accessed. If clearance fails, the deletion will be halted, and you will be notified via your added contact details.
                  </li>
                </ol>
              </div>
            }
            confirmText="Permanently Delete Account"
            cancelText="Cancel"
            isDangerous={true}
            isLoading={isDeletingAccount}
            onConfirm={confirmScheduledDeletion}
            onCancel={closeDeleteModal}
          />

          {/* In-Place Contact OTP Verification Modal */}
          {verifyModal.isOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
              <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-2xl border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-150">
                <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                      <CheckIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        Verify {verifyModal.type === 'email' ? 'Email Address' : 'Phone Number'}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                        {verifyModal.value}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setVerifyModal((prev) => ({ ...prev, isOpen: false, error: null }))}
                    className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    ✕
                  </button>
                </div>

                <div className="py-5 space-y-4">
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                    Enter the 6-digit verification code sent to <strong className="text-slate-900 dark:text-white">{verifyModal.value}</strong>.
                  </p>

                  {verifyModal.devOtp && (
                    <div className="px-3 py-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-xl flex items-center justify-between text-xs">
                      <span className="text-amber-800 dark:text-amber-300 font-medium">Dev Code: <strong className="tracking-widest">{verifyModal.devOtp}</strong></span>
                      <button
                        type="button"
                        onClick={() => setVerifyModal((prev) => ({ ...prev, otp: prev.devOtp, error: null }))}
                        className="text-amber-700 dark:text-amber-400 underline font-semibold hover:text-amber-900 dark:hover:text-amber-200"
                      >
                        Auto-Fill
                      </button>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      maxLength={10}
                      value={verifyModal.otp}
                      autoFocus
                      onChange={(e) => setVerifyModal((prev) => ({ ...prev, otp: e.target.value.replace(/\s+/g, ''), error: null }))}
                      placeholder="Enter 6-digit code"
                      className="w-full text-center text-lg font-mono tracking-widest bg-slate-50 dark:bg-slate-900/80 border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-2.5 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    {verifyModal.error && (
                      <p className="text-xs text-rose-500 font-medium mt-1.5">{verifyModal.error}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs pt-1">
                    <span className="text-slate-500 dark:text-slate-400">Didn't receive the code?</span>
                    <button
                      type="button"
                      disabled={verifyModal.loading}
                      onClick={handleResendOtp}
                      className="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                    >
                      Resend Code
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                  <button
                    type="button"
                    disabled={verifyModal.loading}
                    onClick={() => setVerifyModal((prev) => ({ ...prev, isOpen: false, error: null }))}
                    className="px-4 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={verifyModal.loading || !verifyModal.otp.trim()}
                    onClick={handleConfirmVerification}
                    className="px-5 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-xl shadow-md shadow-indigo-600/20 transition-colors"
                  >
                    {verifyModal.loading ? 'Verifying...' : 'Verify Now'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Api Error Modal with Try Again, Reload Page, Cancel (only if no global auth error modal is active) */}
          <ApiErrorModal
            isOpen={!auth?.authError && !!sectionError}
            title={sectionError?.title || 'Section Loading Failed'}
            message={sectionError?.message || 'An error occurred while loading section data.'}
            onTryAgain={sectionError?.retryFn}
            onReload={() => window.location.reload()}
            onCancel={() => setSectionError(null)}
          />
    </div>
  );
}
